import { Controller } from "@hotwired/stimulus"

// A small, dependency-free square cropper for the User avatar. The server
// still validates the resulting upload; this controller makes the framing
// gesture immediate. HEIC/HEIF that this browser cannot decode gets an
// explicit original-upload fallback instead of a silent dead end.
export default class extends Controller {
  static targets = [
    "dialog", "image", "input", "viewport", "zoom", "fallbackSubmit",
    "selectionError", "editorError", "applyButton"
  ]

  static values = {
    decodeError: String,
    decodeFallback: String,
    processingText: String,
    outputSize: { type: Number, default: 1024 }
  }

  connect() {
    this.fallbackSubmitTarget.classList.add("hidden")
    this.rotation = 0
    this.zoom = 1
    this.offsetX = 0
    this.offsetY = 0
  }

  disconnect() {
    this.releaseObjectUrl()
  }

  choose() {
    this.inputTarget.click()
  }

  selected() {
    const file = this.inputTarget.files[0]
    if (!file) return

    this.hideSelectionError()
    this.fallbackSubmitTarget.classList.add("hidden")
    this.releaseObjectUrl()
    this.objectUrl = URL.createObjectURL(file)
    this.imageTarget.onload = () => this.openEditor()
    this.imageTarget.onerror = () => this.decodeFailed(file)
    this.imageTarget.src = this.objectUrl
  }

  openEditor() {
    this.rotation = 0
    this.zoom = 1
    this.offsetX = 0
    this.offsetY = 0
    this.zoomTarget.value = "1"
    this.editorErrorTarget.classList.add("hidden")
    this.dialogTarget.showModal()
    requestAnimationFrame(() => this.render())
  }

  cancel() {
    this.dialogTarget.close()
    this.inputTarget.value = ""
    this.releaseObjectUrl()
  }

  closed() {
    if (this.applying) return

    this.inputTarget.value = ""
    this.releaseObjectUrl()
  }

  zoomed() {
    this.zoom = Number(this.zoomTarget.value)
    this.clampOffsets()
    this.render()
  }

  rotateLeft() {
    this.rotate(-90)
  }

  rotateRight() {
    this.rotate(90)
  }

  reset() {
    this.rotation = 0
    this.zoom = 1
    this.offsetX = 0
    this.offsetY = 0
    this.zoomTarget.value = "1"
    this.render()
  }

  rotate(degrees) {
    this.rotation = (this.rotation + degrees + 360) % 360
    this.zoom = 1
    this.offsetX = 0
    this.offsetY = 0
    this.zoomTarget.value = "1"
    this.render()
  }

  dragStart(event) {
    event.preventDefault()
    this.viewportTarget.setPointerCapture(event.pointerId)
    this.dragOrigin = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      offsetX: this.offsetX,
      offsetY: this.offsetY
    }
  }

  drag(event) {
    if (!this.dragOrigin || !this.viewportTarget.hasPointerCapture(event.pointerId)) return

    this.offsetX = this.dragOrigin.offsetX + event.clientX - this.dragOrigin.pointerX
    this.offsetY = this.dragOrigin.offsetY + event.clientY - this.dragOrigin.pointerY
    this.clampOffsets()
    this.render()
  }

  dragEnd(event) {
    if (this.viewportTarget.hasPointerCapture(event.pointerId)) {
      this.viewportTarget.releasePointerCapture(event.pointerId)
    }
    this.dragOrigin = null
  }

  async apply() {
    const buttonText = this.applyButtonTarget.innerHTML
    this.applyButtonTarget.disabled = true
    this.applyButtonTarget.innerHTML = this.processingTextValue

    try {
      const blob = await this.croppedBlob()
      const source = this.inputTarget.files[0]
      const stem = source.name.replace(/\.[^.]+$/, "") || "avatar"
      const cropped = new File([blob], `${stem}-avatar.png`, { type: "image/png" })
      const transfer = new DataTransfer()
      transfer.items.add(cropped)
      this.inputTarget.files = transfer.files
      this.applying = true
      this.dialogTarget.close()
      this.element.requestSubmit()
    } catch (_error) {
      this.showEditorError(this.decodeErrorValue)
      this.applyButtonTarget.disabled = false
      this.applyButtonTarget.innerHTML = buttonText
    }
  }

  render() {
    const image = this.imageTarget
    if (!image.naturalWidth || !this.viewportSize) return

    const scale = this.baseScale
    const width = image.naturalWidth * scale
    const height = image.naturalHeight * scale
    image.style.width = `${width}px`
    image.style.height = `${height}px`
    image.style.marginLeft = `${-width / 2}px`
    image.style.marginTop = `${-height / 2}px`
    image.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) rotate(${this.rotation}deg) scale(${this.zoom})`
  }

  clampOffsets() {
    const quarterTurn = this.rotation % 180 !== 0
    const naturalWidth = quarterTurn ? this.imageTarget.naturalHeight : this.imageTarget.naturalWidth
    const naturalHeight = quarterTurn ? this.imageTarget.naturalWidth : this.imageTarget.naturalHeight
    const width = naturalWidth * this.baseScale * this.zoom
    const height = naturalHeight * this.baseScale * this.zoom
    const maxX = Math.max(0, (width - this.viewportSize) / 2)
    const maxY = Math.max(0, (height - this.viewportSize) / 2)

    this.offsetX = Math.max(-maxX, Math.min(maxX, this.offsetX))
    this.offsetY = Math.max(-maxY, Math.min(maxY, this.offsetY))
  }

  croppedBlob() {
    const size = this.outputSizeValue
    const factor = size / this.viewportSize
    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size

    const context = canvas.getContext("2d")
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = "high"
    context.translate(size / 2 + this.offsetX * factor, size / 2 + this.offsetY * factor)
    context.rotate(this.rotation * Math.PI / 180)
    context.scale(this.baseScale * this.zoom * factor, this.baseScale * this.zoom * factor)
    context.drawImage(
      this.imageTarget,
      -this.imageTarget.naturalWidth / 2,
      -this.imageTarget.naturalHeight / 2
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("crop failed")), "image/png")
    })
  }

  decodeFailed(file) {
    const canUploadOriginal = ["image/heic", "image/heif"].includes(file.type) ||
      /\.(heic|heif)$/i.test(file.name)
    this.selectionErrorTarget.textContent = canUploadOriginal ? this.decodeFallbackValue : this.decodeErrorValue
    this.selectionErrorTarget.classList.remove("hidden")
    this.fallbackSubmitTarget.classList.toggle("hidden", !canUploadOriginal)
    this.releaseObjectUrl()
  }

  hideSelectionError() {
    this.selectionErrorTarget.classList.add("hidden")
    this.selectionErrorTarget.textContent = ""
  }

  showEditorError(message) {
    this.editorErrorTarget.textContent = message
    this.editorErrorTarget.classList.remove("hidden")
  }

  releaseObjectUrl() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }

  get viewportSize() {
    return this.viewportTarget.clientWidth
  }

  get baseScale() {
    const quarterTurn = this.rotation % 180 !== 0
    const width = quarterTurn ? this.imageTarget.naturalHeight : this.imageTarget.naturalWidth
    const height = quarterTurn ? this.imageTarget.naturalWidth : this.imageTarget.naturalHeight
    return Math.max(this.viewportSize / width, this.viewportSize / height)
  }
}
